/**
 * PREDICTOR LAB — CodeAI101 chapter 1, the whole mechanism, all the way down.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next".
 *
 * ── THE LEARNER IS THE SAMPLER, AND THAT IS THE DESIGN ────────────────────────────────────────
 * The obvious build is a button that generates a sentence. It would be worse. §1's claim is that
 * the machine has no idea whether what it says is true — and a learner watching output appear
 * cannot see the absence of knowing; they just see text, which is the very illusion the section is
 * about. So at every step the table's ENTIRE opinion is on screen: four or five candidate words
 * with their counts and shares, and the learner picks. Twelve picks later they have a fluent
 * sentence they assembled themselves out of nothing but frequencies, and they know it, because
 * they were the only judgement in the loop.
 *
 * ── THE TOY LOOPS, AND THE LOOP IS THE HONEST PART ────────────────────────────────────────────
 * "Let it run" takes the most likely word every time and folds into "model is the model is the
 * model" within six words. That is not a defect to hide behind a random sampler — it is the one
 * thing this toy can prove about a real model by contrast: this table remembers one word, a
 * language model predicts "based on every token that came before it". The loop is where the toy
 * ends and the module's sentence begins, and the surface says exactly that when it happens.
 *
 * ── WHAT IT WILL NOT SAY ──────────────────────────────────────────────────────────────────────
 * Beside every sentence produced, the same line: nothing here checked whether it is true, because
 * there is nothing here that could. Not as a disclaimer in small print — as the readout, in the
 * same weight as the sentence, because it is the finding.
 */

import { useMemo, useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import CORPUS from '../codeai101-m1.txt?raw';
import {
  buildTable, nextWords, seeds, walk, WHY_IT_LOOPS, CONTEXT_QUOTE, IS_IT_TRUE,
  THE_LINE, THE_MECHANISM, NO_IDEA, IMITATING, CHECK_ELSEWHERE,
  WRAPPER_TELLS, CANNOT_SURVIVE, NEVER_ADMISSIBLE, SOURCE_1, SOURCE_3,
} from './predictor.mjs';

export default function PredictorLab() {
  const built = useMemo(() => buildTable(CORPUS), []);
  const openings = useMemo(() => seeds(built), [built]);
  const [chain, setChain] = useState([]);
  const [auto, setAuto] = useState(null);
  const [typed, setTyped] = useState('');

  const cur = chain[chain.length - 1];
  const options = cur ? nextWords(built.table, cur, 6) : [];
  const sentence = chain.join(' ');

  const start = (w) => { setAuto(null); setChain([w]); };
  const pick = (w) => setChain((c) => [...c, w]);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Predictor lab · the whole mechanism</div>
      <h2 className="g-h2 lab-h">It is guessing the next word. That is all it is doing.</h2>
      <p className="g-body"><Value of={cited(THE_MECHANISM, SOURCE_1)} /></p>
      <p className="g-body">
        Below is a table built by reading this module once and counting which word followed which.
        That is the entire model. Pick a word to start, then keep choosing what comes next — you
        will see everything it knows at every step, which is not much.
      </p>

      {/* ── START ──
       * TYPE ANY WORD, not just the offered ones. The founder asked whether the labs take input or
       * only simulate, and here the answer was "only the buttons" — which quietly hid the most
       * useful thing this table can demonstrate. A word the module never used returns nothing at
       * all, and that emptiness is the lesson: the model's world is exactly its training text and
       * has a hard edge. Offering only words that work would have shown a model with no edge. */}
      <div className="lab-task-head">
        <span className="g-label">Start with</span>
        {openings.map((w) => (
          <button key={w} className={`g-btn ${chain[0] === w ? 'g-btn-primary' : ''}`}
            onClick={() => start(w)}>{w}</button>
        ))}
      </div>
      <div className="lab-task-head">
        <span className="g-label">…or type any word at all</span>
        <input className="morse-input prd-in" value={typed} placeholder="try a word the module never used"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim()) start(typed.trim()); }} />
        <button className="g-btn" disabled={!typed.trim()} onClick={() => start(typed.trim())}>Start</button>
      </div>
      {chain.length === 1 && options.length === 0 && (
        <p className="g-body prd-edge">
          The table has never seen “{chain[0]}”. Not once, in the whole module. A model knows exactly
          the text it was built from and not one word more — and this is what that edge looks like
          from the inside, with nothing behind it.
        </p>
      )}

      {chain.length > 0 && (
        <>
          {/* ── THE SENTENCE SO FAR ── */}
          <div className="lab-task">
            <div className="g-label">What you have assembled</div>
            <p className="prd-sentence">{sentence}<span className="prd-caret" /></p>
            <div className="lab-readout">
              <div><span className="g-label">Words</span><Value of={computed(String(chain.length))} /></div>
              <div><span className="g-label">Judgement used</span>
                <Value of={computed('yours, entirely')} /></div>
            </div>
          </div>

          {/* ── THE MODEL'S ENTIRE OPINION ── */}
          <div className="lab-task">
            <div className="g-label">Everything the table knows about what follows “{cur}”</div>
            {options.length === 0 ? (
              <p className="g-body">
                Nothing followed “{cur}” in the whole module. The table has run out — which is what
                a model with no entry looks like from inside.
              </p>
            ) : (
              <div className="prd-opts">
                {options.map((o) => (
                  <button key={o.word} className="prd-opt" onClick={() => pick(o.word)}>
                    <span className="prd-w">{o.word}</span>
                    <span className="prd-bar"><span style={{ width: `${Math.max(4, o.share)}%` }} /></span>
                    <span className="prd-n">{o.count}× · {o.share}%</span>
                  </button>
                ))}
              </div>
            )}
            <p className="g-body g-dim prd-note">
              Those counts are the model. There is no other part. It has no opinion about whether any
              of these words would make the sentence true — it has never had a concept of true.
            </p>
          </div>

          {/* ── AND WHAT IT CANNOT TELL YOU ── */}
          <div className="lab-task">
            <div className="g-label">Is what you just built true?</div>
            <Value of={refused(IS_IT_TRUE)} />
            <p className="g-body"><Value of={cited(NO_IDEA, SOURCE_1)} /></p>
            <p className="g-body g-dim"><Value of={cited(CHECK_ELSEWHERE, SOURCE_1)} /></p>
          </div>

          {/* ── LET IT DRIVE, AND WATCH IT FOLD ── */}
          <div className="lab-task">
            <div className="lab-task-head">
              <span className="g-label">Or let it choose for itself — always the most likely word</span>
              <button className="g-btn" onClick={() => setAuto(walk(built, chain[0], 14))}>Let it run</button>
            </div>
            {auto && (
              <>
                <p className="prd-sentence prd-auto">{auto.words.join(' ')}</p>
                {auto.looped && (
                  <>
                    <p className="g-body prd-loop">It folded in half. <Value of={cited(WHY_IT_LOOPS, SOURCE_1)} /></p>
                    <p className="g-body g-dim">
                      The module's own words for what a real one does:
                      <Value of={cited(CONTEXT_QUOTE, SOURCE_1)} />
                    </p>
                  </>
                )}
                {auto.ranOut && <p className="g-body">It ran out of table — no word was ever recorded after that one.</p>}
                <p className="g-body g-dim">Press it twice and you get the same sentence. Nothing is
                  hiding in randomness; there is only the table.</p>
              </>
            )}
          </div>
        </>
      )}

      <p className="g-body prd-punch"><Value of={cited(THE_LINE, SOURCE_1)} /></p>
      <p className="g-body g-dim"><Value of={cited(IMITATING, SOURCE_1)} /></p>

      {/* ── §3 — the checklist a learner can carry into a sales meeting ── */}
      <div className="lab-task">
        <div className="g-label">Three things that tell you a product is a wrapper</div>
        <ol className="prd-tells">
          {WRAPPER_TELLS.map((t) => (
            <li key={t.n}><b>{t.name}.</b> <Value of={cited(t.quote, SOURCE_3)} /></li>
          ))}
        </ol>
        <p className="g-body"><Value of={cited(CANNOT_SURVIVE, SOURCE_3)} /></p>
        <p className="g-body"><Value of={cited(NEVER_ADMISSIBLE, SOURCE_3)} /></p>
      </div>

      <p className="g-body g-dim lab-why">
        This is a <b>bigram table</b>, not a language model, and the difference is not modesty. It
        remembers one word; a language model predicts from everything that came before, across a
        corpus of billions of pages rather than one module of three thousand words. Same mechanism —
        count, weigh, pick — and incomparable scale. Calling it “an LLM in your browser” would be
        the wrapper marketing you were just taught to spot.
        <br />
        Its corpus is this module and nothing else. It cannot produce a sentence about medicine, tax
        or a contract, which is deliberate: a lab that generated confident wrong answers in those
        domains to demonstrate the danger would be manufacturing the exact artefact the chapter warns
        about, and a screenshot of it would carry no label.
      </p>
    </div>
  );
}
